import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaBuilding,
  FaMicrophone,
  FaSearch,
  FaEye,
  FaTimes,
  FaEdit
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ModifiedVoice = () => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Search/filter states
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;

  useEffect(() => {
    fetchModifiedVoices();
  }, []);

  // Set startDate and endDate to today when todayOnly is checked
  useEffect(() => {
    if (todayOnly) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;
      setStartDate(todayStr);
      setEndDate(todayStr);
    }
  }, [todayOnly]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, startDate, endDate, todayOnly]);

  // Date helper function
  const toDate = (dateStr, endOfDay = false) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };

  const fetchModifiedVoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/new/ryv/ads?all=true`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("Fetched Modified Voices Data:", response);

      // Handle different response structures and filter for modified voices
      const voicesData = response.data.data || [];
      const modifiedVoices = voicesData.filter(voice => voice.status === "modified");
      setVoices(Array.isArray(modifiedVoices) ? modifiedVoices : []);
    } catch (err) {
      console.error("Error fetching modified voices:", err);
      setError(err.response?.data?.message || "Failed to fetch modified voices");
      toast.error("Failed to load modified voices");
    } finally {
      setLoading(false);
    }
  };

  // Filtered voices based on search and date filters
  const filteredVoices = voices.filter((voice) => {
    // Status filter - only show modified voices
    if (voice.status !== "modified") return false;

    // Search filter
    const q = searchText.trim().toLowerCase();
    if (q) {
      // Check voice ID, state, city, or submitter name
      if (voice._id && voice._id.toString().toLowerCase().includes(q)) return true;
      if (voice.state && voice.state.toLowerCase().includes(q)) return true;
      if (voice.city && voice.city.toLowerCase().includes(q)) return true;
      if (voice.submittedBy?.name && voice.submittedBy.name.toLowerCase().includes(q)) return true;
      // Check admin selected states
      if (voice.adminSelectState && voice.adminSelectState.some(state =>
        state.toLowerCase().includes(q)
      )) return true;
      // Check admin selected cities
      if (voice.adminSelectCities && voice.adminSelectCities.some(city =>
        city.toLowerCase().includes(q)
      )) return true;
      // Check admin selected pincode
      if (voice.adminSelectPincode && voice.adminSelectPincode.toString().includes(q)) return true;
      return false;
    }

    // Date filter
    const voiceDate = toDate(voice.createdAt);
    const filterStart = toDate(startDate);
    const filterEnd = toDate(endDate, true);

    // Skip voices with invalid dates if filters are active
    if ((todayOnly || filterStart || filterEnd) && !voiceDate) {
      return false;
    }

    // Apply date filters
    if (filterStart && voiceDate < filterStart) return false;
    if (filterEnd && voiceDate > filterEnd) return false;

    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredVoices.length / rowsPerPage);
  const paginatedVoices = filteredVoices.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle view details
  const handleViewDetails = (voice) => {
    setSelectedVoice(voice);
    setShowModal(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setSelectedVoice(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        <span className="ml-3 text-gray-600">
          Loading modified voices...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">⚠️ Error</div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchModifiedVoices}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 sm:p-6">
        {/* Search and Filter Controls */}
        <div className="mb-6 space-y-4">
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="relative w-full sm:w-96">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="w-5 h-5 text-gray-500" />
              </div>
              <input
                type="text"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search by Voice ID, State, City, Admin Targeting..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <button
              onClick={() => {
                setSearchText("");
                setStartDate("");
                setEndDate("");
                setTodayOnly(false);
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>

          {/* Date Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="todayOnly"
                checked={todayOnly}
                onChange={(e) => setTodayOnly(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="todayOnly" className="text-sm font-medium text-gray-700">
                Today Only
              </label>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">From:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={todayOnly}
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">To:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={todayOnly}
              />
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row sm:justify-end gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              className="flex-1 sm:flex-none bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
              onClick={() => navigate("/admin/pendingvoice")}
            >
              Pending
            </button>
            <button
              className="flex-1 sm:flex-none bg-green-500 hover:bg-green-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
              onClick={() => navigate("/admin/approvedvoice")}
            >
              Approved
            </button>
            <button
              className="flex-1 sm:flex-none bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
              onClick={() => navigate("/admin/runningvoice")}
            >
              Running
            </button>
            <button
              className="flex-1 sm:flex-none bg-red-500 hover:bg-red-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
              onClick={() => navigate("/admin/rejectedvoice")}
            >
              Rejected
            </button>
            <button
              className="flex-1 sm:flex-none bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded shadow font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
              onClick={() => navigate("/admin/completedvoice")}
            >
              Completed
            </button>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
              Modified Raise Your Voice
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              View all Raise Your Voice posts that have been modified by admin with targeting configuration
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
              {filteredVoices.length === voices.length
                ? `Total: ${voices.length}`
                : `Showing: ${filteredVoices.length} of ${voices.length}`
              }
            </div>
            <button
              onClick={fetchModifiedVoices}
              className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <FaSearch />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Conference Table Design */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
          </div>
        ) : paginatedVoices.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <FaEdit className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">
              No Modified Raise Your Voice Posts
            </h3>
            <p className="text-gray-500">
              {filteredVoices.length === 0
                ? "There are currently no modified Raise Your Voice posts."
                : "No Raise Your Voice posts match your current filters."
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
              <thead>
                <tr className="bg-gray-100 text-sm text-gray-700">
                  <th className="px-4 py-3 text-left">Voice ID</th>
                  <th className="px-4 py-3 text-left">Date & Time</th>
                  <th className="px-4 py-3 text-left">Location</th>
                  <th className="px-4 py-3 text-left">Admin Targeting</th>
                  <th className="px-4 py-3 text-left">Modified At</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVoices.map((voice) => (
                  <tr key={voice._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">{voice.postId || voice._id.slice(-8)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center text-sm">
                        <FaCalendarAlt className="mr-1 text-blue-500" />
                        {new Date(voice.createdAt).toLocaleDateString('en-IN')}
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <FaClock className="mr-1" />
                        {new Date(voice.createdAt).toLocaleTimeString('en-IN')}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center text-sm">
                        <FaMapMarkerAlt className="mr-1 text-green-500" />
                        {voice.city}, {voice.state}
                      </div>
                      <div className="text-xs text-gray-500">{voice.residenceAddress || voice.place || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-2">
                        {/* All States */}
                        {voice.allStates && (
                          <div className="flex items-center">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              All States
                            </span>
                          </div>
                        )}

                        {/* Selected States */}
                        {!voice.allStates && voice.adminSelectState && voice.adminSelectState.length > 0 && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">States:</div>
                            <div className="flex flex-wrap gap-1">
                              {voice.adminSelectState.map((state, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                                >
                                  {state}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Selected Cities */}
                        {voice.adminSelectCities && voice.adminSelectCities.length > 0 && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Cities:</div>
                            <div className="flex flex-wrap gap-1">
                              {voice.adminSelectCities.slice(0, 3).map((city, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {city}
                                </span>
                              ))}
                              {voice.adminSelectCities.length > 3 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  +{voice.adminSelectCities.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Pincode */}
                        {voice.adminSelectPincode && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Pincode:</div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {voice.adminSelectPincode}
                            </span>
                          </div>
                        )}

                        {/* Selected Reporters Count */}
                        {voice.reporterId && voice.reporterId.length > 0 && (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Reporters:</div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {voice.reporterId.length} selected
                            </span>
                          </div>
                        )}

                        {/* No targeting */}
                        {!voice.allStates &&
                          (!voice.adminSelectState || voice.adminSelectState.length === 0) &&
                          (!voice.adminSelectCities || voice.adminSelectCities.length === 0) &&
                          !voice.adminSelectPincode &&
                          (!voice.reporterId || voice.reporterId.length === 0) && (
                            <span className="text-gray-500 text-sm">No targeting set</span>
                          )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {voice.adminAction?.actionDate
                        ? new Date(voice.adminAction.actionDate).toLocaleDateString('en-IN')
                        : "N/A"
                      }
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                        Modified
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewDetails(voice)}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm flex items-center"
                      >
                        <FaEye className="mr-1" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0">
            {/* Page Info */}
            <div className="text-sm text-gray-600">
              Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredVoices.length)} of {filteredVoices.length} voices
            </div>

            {/* Pagination Navigation */}
            <nav className="inline-flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded border transition-colors ${currentPage === pageNum
                        ? 'border-blue-400 bg-blue-300 text-blue-900 font-bold'
                        : 'border-blue-400 text-blue-800 bg-white hover:bg-blue-100'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Voice Details Modal */}
      {showModal && selectedVoice && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Modified Voice Details - {selectedVoice.postId || selectedVoice._id.slice(-8)}
                  </h3>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>

                {/* Voice Information */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Voice Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-gray-600">Submitter:</span>
                      <p className="text-gray-900">{selectedVoice.name || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Date & Time:</span>
                      <p className="text-gray-900">{new Date(selectedVoice.createdAt).toLocaleDateString('en-IN')} at {new Date(selectedVoice.createdAt).toLocaleTimeString('en-IN')}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Location:</span>
                      <p className="text-gray-900">{selectedVoice.city}, {selectedVoice.state}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Residence Address:</span>
                      <p className="text-gray-900">{selectedVoice.residenceAddress || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="font-medium text-gray-600">Description:</span>
                    <p className="text-gray-900">{selectedVoice.description || "No description available"}</p>
                  </div>
                  {selectedVoice.landmark && (
                    <div className="mt-3">
                      <span className="font-medium text-gray-600">Nearby Landmark:</span>
                      <p className="text-gray-900">{selectedVoice.landmark}</p>
                    </div>
                  )}
                </div>

                {/* Submitter Information */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-blue-800 mb-3">Submitter Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-blue-600">Name:</span>
                      <p className="text-blue-900">{selectedVoice.submittedBy?.name || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-600">Email:</span>
                      <p className="text-blue-900">{selectedVoice.submittedBy?.email || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-600">Organization:</span>
                      <p className="text-blue-900">{selectedVoice.submittedBy?.organization || "N/A"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-blue-600">Raise Your Voice ID:</span>
                      <p className="text-blue-900">{selectedVoice._id || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Admin Modification Details */}
                <div className="mb-6 p-4 bg-orange-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-orange-800 mb-3">Admin Modification Details</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="font-medium text-orange-600">Status:</span>
                      <span className="ml-2 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                        Modified
                      </span>
                    </div>
                    {selectedVoice.adminAction?.actionDate && (
                      <div>
                        <span className="font-medium text-orange-600">Modified At:</span>
                        <p className="text-orange-900">{new Date(selectedVoice.adminAction.actionDate).toLocaleString('en-IN')}</p>
                      </div>
                    )}
                    {selectedVoice.adminAction?.note && (
                      <div>
                        <span className="font-medium text-orange-600">Admin Note:</span>
                        <p className="text-orange-900">{selectedVoice.adminAction.note}</p>
                      </div>
                    )}

                    {/* Targeting Configuration */}
                    <div className="border-t border-orange-200 pt-4">
                      <h5 className="font-semibold text-orange-700 mb-3">Targeting Configuration</h5>

                      {/* All States */}
                      {selectedVoice.allStates && (
                        <div className="mb-3">
                          <span className="font-medium text-orange-600">Targeting:</span>
                          <span className="ml-2 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            All States
                          </span>
                        </div>
                      )}

                      {/* Selected States */}
                      {!selectedVoice.allStates && selectedVoice.adminSelectState && selectedVoice.adminSelectState.length > 0 && (
                        <div className="mb-3">
                          <span className="font-medium text-orange-600">Selected States:</span>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {selectedVoice.adminSelectState.map((state, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800"
                              >
                                {state}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Selected Cities */}
                      {selectedVoice.adminSelectCities && selectedVoice.adminSelectCities.length > 0 && (
                        <div className="mb-3">
                          <span className="font-medium text-orange-600">Selected Cities:</span>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {selectedVoice.adminSelectCities.map((city, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                              >
                                {city}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Pincode */}
                      {selectedVoice.adminSelectPincode && (
                        <div className="mb-3">
                          <span className="font-medium text-orange-600">Pincode:</span>
                          <span className="ml-2 px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                            {selectedVoice.adminSelectPincode}
                          </span>
                        </div>
                      )}

                      {/* Selected Reporters */}
                      {selectedVoice.reporterId && selectedVoice.reporterId.length > 0 && (
                        <div className="mb-3">
                          <span className="font-medium text-orange-600">Selected Reporters:</span>
                          <span className="ml-2 px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                            {selectedVoice.reporterId.length} reporter(s) selected
                          </span>
                        </div>
                      )}

                      {/* No targeting */}
                      {!selectedVoice.allStates &&
                        (!selectedVoice.adminSelectState || selectedVoice.adminSelectState.length === 0) &&
                        (!selectedVoice.adminSelectCities || selectedVoice.adminSelectCities.length === 0) &&
                        !selectedVoice.adminSelectPincode &&
                        (!selectedVoice.reporterId || selectedVoice.reporterId.length === 0) && (
                          <div className="mb-3">
                            <span className="font-medium text-orange-600">Targeting:</span>
                            <span className="ml-2 text-gray-500">No targeting configuration set</span>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {/* Admin Note */}
                {selectedVoice.adminNote && (
                  <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
                    <h4 className="text-lg font-semibold text-yellow-800 mb-3">Additional Admin Note</h4>
                    <p className="text-yellow-900">{selectedVoice.adminNote}</p>
                  </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModifiedVoice;
